export interface AnomalyBarDataPoint {
  date: number
}

/**
 * Builds an onBarClick handler for the anomaly detection charts (StackedColumnsDateChart) that
 * only fires for a genuinely non-zero column - clicking any (non-zero) column should switch the
 * examined date, not just an anomaly one, but a day whose only populated series is zero must
 * stay a no-op.
 * isClickableBar is supplied per chart since the stacked series (and their keys) differ per chart
 * (e.g. trainingCount/recentCount/anomalyCount for the event charts, anomalyMobile/anomalyWifi for
 * the data usage chart). Returns undefined when onDayClick is not supplied, so callers can pass
 * this straight through to StackedColumnsDateChart's optional onBarClick without an extra
 * conditional.
 */
export function getAnomalyBarClickHandler<Data extends AnomalyBarDataPoint>(
  isClickableBar: (dataPoint: Data) => boolean,
  onDayClick: ((date: number) => void) | undefined
): ((dataPoint: Data) => void) | undefined {
  if (!onDayClick) {
    return undefined;
  }
  return (dataPoint: Data) => {
    if (isClickableBar(dataPoint)) {
      onDayClick(dataPoint.date);
    }
  };
}

export interface AnomalyDataPoint {
  date: string,
  isAnomaly: boolean
}

/**
 * Returns the most recent date among a top chart's own data points flagged as an anomaly, or
 * undefined if none are - used by DeviceAnomalyExaminationPage to default the examined day when
 * no explicit date is supplied (e.g. a link from the top-level anomalies table). The minimal
 * `date`/`isAnomaly` shape is satisfied by both IDeviceAnomalyEventDataPoint and
 * IDeviceAnomalyDataUsageDataPoint, so every anomaly type's top chart can share this helper.
 * Does not assume the input is already sorted by date.
 */
export function getLatestAnomalyDate(points: AnomalyDataPoint[]): number | undefined {
  return points.reduce<number | undefined>((latest, point) => {
    if (!point.isAnomaly) {
      return latest;
    }
    const date = Number(point.date);
    return latest === undefined || date > latest ? date : latest;
  }, undefined);
}
